import React, { Fragment, useEffect, useRef, useState } from 'react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import { compose } from 'recompose';
import config from '../../../config';

import { BaseError as Error } from '@/se/components/errors/Error';
import { withSession } from '../../../state/Session';
import { unpackSessionObject } from '../../pages/unpackSessionObject';
import Spinner from '@/se/components/Spinner';
import { withScope } from '../../../contexts/ScopeContext';
import { getFileContentType } from './formats';

export const UPLOAD_RESOURCE = {
  SCHEDULE: 'schedule',
  PHYSICIAN: 'physician',
};

const GENERIC_UPLOAD_ERROR = 'Failed to complete the upload.';
const GENERIC_CONFIRMATION_ERROR = 'Unable to confirm the upload.';

// The API routes uploads by request entity media type and has no .pdf X-Filename fallback,
// so every request must carry an explicit Content-Type.
const buildUploadHeaders = file => {
  const headers = { 'X-Filename': file.name };
  const contentType = getFileContentType(file);

  if (contentType) {
    headers['Content-Type'] = contentType;
  }

  return headers;
};

// Errors are returned as {error: "<message>"}; fall back to a generic sentence rather than
// leaking raw statusText/network errors when the body is missing or unparseable.
const parseErrorMessage = async (response, fallback) => {
  try {
    const body = await response.json();
    return (body && body.error) || fallback;
  } catch (_error) {
    return fallback;
  }
};

const Uploader = ({ UploadConfirmation, filePicker, file, session, scope, onCancel, onDone, resource }) => {
  // ScheduleUploadConfirmation seeds its selected-dates state from a useState initializer that
  // only runs on mount, so without a remount, confirming file B after switching from file A can
  // submit file A's stale date selection against file B's dry-run. Two layers guard against
  // that: (1) the clear below drops uploadResult, unmounting UploadConfirmation behind the
  // spinner while the new file's dry-run is in flight, and (2) uploadId is bumped on every new
  // dry-run result and passed as `key`, forcing a remount even if a future refactor of the clear
  // stops unmounting the component in between. uploadId is tracked in a ref (not the state object
  // below) specifically so it survives the state clear and stays genuinely monotonic.
  const [{ uploadError, uploadResult, confirmationError, isUploading }, setState] = useState({});
  const lastUploadedFileRef = useRef(null);
  const uploadIdRef = useRef(0);

  useEffect(() => {
    const hospitalId = get(scope, 'hospital.id');

    if (!hospitalId || !file) {
      return;
    }

    // Avoid re-uploading if the file has not actually changed
    if (lastUploadedFileRef.current === file) {
      return;
    }

    lastUploadedFileRef.current = file;

    // Clear the previous file's result/error immediately: otherwise the dialog keeps showing
    // stale dry-run results (and a stale UploadConfirmation instance) while the new file's
    // dry-run request is in flight.
    setState({});

    const controller = new AbortController();
    const signal = controller.signal;

    (async () => {
      try {
        const result = await fetch(
          `${config.apiURL}/${resource}?hospital=${hospitalId}&token=${encodeURIComponent(get(session, 'token'))}`,
          {
            method: 'POST',
            body: file,
            signal,
            headers: buildUploadHeaders(file),
          }
        );

        if (Math.floor(result.status / 100) === 2) {
          const body = await result.json();
          uploadIdRef.current += 1;
          setState({ uploadResult: body });
        } else {
          const message = await parseErrorMessage(result, GENERIC_UPLOAD_ERROR);
          setState({ uploadError: message });
        }
      } catch (error) {
        console.error(error);
        setState({ uploadError: GENERIC_UPLOAD_ERROR });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [file, scope, session, resource]);

  const handleConfirmation = async (result, urlParameters = '') => {
    try {
      const hospitalId = get(scope, 'hospital.id');

      if (!hospitalId) {
        return;
      }

      if (isArray(result) && result.length === 0) {
        onDone();
        return;
      }

      // Set uploading state to show spinner and disable button
      setState({ uploadResult, isUploading: true });

      const response = await fetch(
        `${config.apiURL}/${resource}?hospital=${hospitalId}&token=${encodeURIComponent(
          get(session, 'token')
        )}${urlParameters}`,
        {
          method: 'POST',
          body: file,
          headers: buildUploadHeaders(file),
        }
      );

      if (!response.ok) {
        const message = await parseErrorMessage(response, GENERIC_CONFIRMATION_ERROR);
        setState({ uploadResult, confirmationError: message, isUploading: false });
        return;
      }

      onDone(result);
    } catch (error) {
      console.error(error);
      setState({ uploadResult, confirmationError: GENERIC_CONFIRMATION_ERROR, isUploading: false });
    }
  };

  return uploadError ? (
    <Fragment>
      <Error>{uploadError}</Error>
    </Fragment>
  ) : uploadResult ? (
    <Fragment>
      {confirmationError && <Error>{confirmationError}</Error>}
      <UploadConfirmation
        key={uploadIdRef.current}
        filePicker={filePicker}
        file={file}
        onSubmit={handleConfirmation}
        onCancel={onCancel}
        isUploading={isUploading}
        {...uploadResult}
      />
    </Fragment>
  ) : (
    <Spinner size="2rem" />
  );
};

export default compose(withSession(unpackSessionObject), withScope)(Uploader);
